import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Frequencia } from './frequencia';
import { AlunoFrequencia } from './aluno-frequencia';
import {
  FaltaMatriculadoConsolidado,
  FrequenciaFaltasConsolidado,
} from './frequencia-faltas-consolidado';
import { environment } from '../../../environments/environment';
import { ChamadaRemovidaDto, ChamadaTurmaDto, DepuracaoChamadasDto } from './chamada-turma.dto';
import { FaltasCatequizandoDto } from './faltas-catequizando.dto';
import { MatrizFrequenciaTurmaDto } from './matriz-frequencia.dto';

export type ContextoFrequenciaTurma = {
  turmaId: string;
  data: string;
  turmaNome?: string;
};

@Injectable({
  providedIn: 'root',
})
export class FrequenciaService {
  private readonly apiUrl = `${environment.api}/frequencia`;

  constructor(private http: HttpClient) {}

  /**
   * GET /frequencia/{turmaId}/{data}  (data: yyyy-MM-dd)
   */
  carregarFrequencia(turmaId: string, data: string): Observable<Frequencia> {
    const turma = encodeURIComponent(turmaId);
    const dataSeg = encodeURIComponent(data);
    return this.http
      .get<unknown>(`${this.apiUrl}/${turma}/${dataSeg}`)
      .pipe(map((body) => this.normalizarRespostaGet(body, turmaId, data)));
  }

  /**
   * GET /frequencia/{turmaId}/consolidado-faltas
   */
  carregarConsolidadoFaltas(turmaId: string): Observable<FrequenciaFaltasConsolidado> {
    const turma = encodeURIComponent(turmaId);
    return this.http
      .get<unknown>(`${this.apiUrl}/${turma}/consolidado-faltas`)
      .pipe(map((body) => this.normalizarConsolidadoFaltas(body)));
  }

  /**
   * GET /frequencia/{turmaId}/catequizandos/{pessoaId}/faltas
   * — dias em que o catequizando recebeu falta na turma.
   */
  listarFaltasDoCatequizando(turmaId: string, pessoaId: string): Observable<FaltasCatequizandoDto> {
    return this.http.get<FaltasCatequizandoDto>(
      `${this.apiUrl}/${encodeURIComponent(turmaId)}/catequizandos/${encodeURIComponent(pessoaId)}/faltas`,
    );
  }

  /**
   * GET /frequencia/{turmaId}/matriz — linhas = matriculados, colunas = datas com chamada.
   * Células `virtual = true` são faltas sem registro (a API não grava nada para montá-las).
   */
  carregarMatriz(turmaId: string): Observable<MatrizFrequenciaTurmaDto> {
    return this.http.get<MatrizFrequenciaTurmaDto>(
      `${this.apiUrl}/${encodeURIComponent(turmaId)}/matriz`,
    );
  }

  /**
   * GET /frequencia/{turmaId}/chamadas — datas com chamada registrada, da mais recente
   * para a mais antiga, com o resumo de presenças/faltas.
   */
  listarChamadas(turmaId: string): Observable<ChamadaTurmaDto[]> {
    return this.http
      .get<ChamadaTurmaDto[]>(`${this.apiUrl}/${encodeURIComponent(turmaId)}/chamadas`)
      .pipe(map((res) => (Array.isArray(res) ? res : [])));
  }

  /**
   * DELETE /frequencia/{turmaId}/{data} — apaga **todos** os registros da turma naquela data
   * (desfaz a chamada do dia).
   */
  excluirChamada(turmaId: string, data: string): Observable<ChamadaRemovidaDto> {
    return this.http.delete<ChamadaRemovidaDto>(
      `${this.apiUrl}/${encodeURIComponent(turmaId)}/${encodeURIComponent(data)}`,
    );
  }

  /**
   * POST /frequencia/{turmaId}/depurar-chamadas-sem-presenca — apaga as chamadas da turma em que
   * **nenhum** catequizando foi marcado presente (chamada começada e não concluída).
   */
  depurarChamadasSemPresenca(turmaId: string): Observable<DepuracaoChamadasDto> {
    return this.http.post<DepuracaoChamadasDto>(
      `${this.apiUrl}/${encodeURIComponent(turmaId)}/depurar-chamadas-sem-presenca`,
      {},
    );
  }

  /**
   * POST /frequencia — um único registro (mesmo formato de um item do GET).
   */
  salvarLinha(linha: AlunoFrequencia, ctx: ContextoFrequenciaTurma): Observable<void> {
    const item: Record<string, unknown> = {
      turmaId: linha.turmaId ?? ctx.turmaId,
      turmaNome: linha.turmaNome ?? ctx.turmaNome,
      pessoaId: linha.pessoaId,
      pessoaNome: linha.pessoaNome ?? '',
      data: linha.data ?? ctx.data,
      presente: linha.presente === true,
      presenteMissa: linha.presenteMissa === true,
    };
    if (linha.id) item['id'] = linha.id;
    if (linha.fotoDocumentoId) item['fotoDocumentoId'] = linha.fotoDocumentoId;
    return this.http.post<void>(this.apiUrl, item);
  }

  private normalizarConsolidadoFaltas(body: unknown): FrequenciaFaltasConsolidado {
    const o = body as Record<string, unknown>;
    const matRaw = o['matriculados'];
    const matriculados = Array.isArray(matRaw)
      ? matRaw.map((row) => {
          const r = row as Record<string, unknown>;
          const pid = r['pessoaId'];
          const telRaw = r['pessoaTelefone'];
          const pessoaTelefone =
            telRaw !== undefined && telRaw !== null && String(telRaw).trim() !== ''
              ? String(telRaw)
              : null;
          return new FaltaMatriculadoConsolidado({
            pessoaId: pid !== undefined && pid !== null ? String(pid) : undefined,
            pessoaNome: (r['pessoaNome'] as string) ?? '',
            pessoaTelefone,
            totalFaltas: Number(r['totalFaltas'] ?? 0),
            dataUltimoRegistro: (r['dataUltimoRegistro'] as string) ?? undefined,
            faltouUltimoEncontro: r['faltouUltimoEncontro'] === true,
          });
        })
      : [];
    const tid = o['turmaId'];
    return new FrequenciaFaltasConsolidado({
      turmaId: tid !== undefined && tid !== null ? String(tid) : undefined,
      turmaNome: (o['turmaNome'] as string) ?? undefined,
      frequenciaMissa: o['frequenciaMissa'] === true,
      matriculados,
    });
  }

  private normalizarRespostaGet(body: unknown, turmaId: string, data: string): Frequencia {
    if (Array.isArray(body)) {
      const first = (body[0] ?? {}) as Record<string, unknown>;
      return new Frequencia({
        turmaId: (first['turmaId'] as string) ?? turmaId,
        turmaNome: first['turmaNome'] as string | undefined,
        data: (first['data'] as string) ?? data,
        alunos: body.map((a) => this.normalizarAluno(a)),
      });
    }
    const o = body as Record<string, unknown>;
    const alunosRaw = o['alunos'] ?? o['itens'] ?? [];
    const alunos = Array.isArray(alunosRaw) ? alunosRaw.map((a) => this.normalizarAluno(a)) : [];
    return new Frequencia({
      turmaId: (o['turmaId'] as string) ?? turmaId,
      turmaNome: o['turmaNome'] as string | undefined,
      data: (o['data'] as string) ?? data,
      alunos,
      catequistas: o['catequistas'] as string[] | undefined,
    });
  }

  private normalizarAluno(raw: unknown): AlunoFrequencia {
    const a = raw as Record<string, unknown>;
    let presente = false;
    if (typeof a['presente'] === 'boolean') {
      presente = a['presente'] as boolean;
    } else if (typeof a['presenca'] === 'boolean') {
      presente = a['presenca'] as boolean;
    }

    let presenteMissa = false;
    if (typeof a['presenteMissa'] === 'boolean') {
      presenteMissa = a['presenteMissa'] as boolean;
    }

    let frequenciaMissa = false;
    if (typeof a['frequenciaMissa'] === 'boolean') {
      frequenciaMissa = a['frequenciaMissa'] as boolean;
    }

    const pessoaId = a['pessoaId'];
    const pessoaIdStr =
      pessoaId !== undefined && pessoaId !== null ? String(pessoaId) : undefined;

    const nome =
      (a['pessoaNome'] as string) ||
      (a['nome'] as string) ||
      '';

    const telRaw = a['pessoaTelefone'];
    const pessoaTelefone =
      telRaw !== undefined && telRaw !== null && String(telRaw).trim() !== ''
        ? String(telRaw)
        : null;

    const fotoRaw = a['fotoDocumentoId'];
    const fotoDocumentoId =
      fotoRaw !== undefined && fotoRaw !== null && String(fotoRaw).trim() !== ''
        ? String(fotoRaw)
        : undefined;

    return new AlunoFrequencia({
      id: a['id'] !== undefined && a['id'] !== null ? String(a['id']) : undefined,
      turmaId: a['turmaId'] !== undefined ? String(a['turmaId']) : undefined,
      turmaNome: (a['turmaNome'] as string) ?? undefined,
      pessoaId: pessoaIdStr,
      pessoaNome: nome,
      pessoaTelefone,
      data: (a['data'] as string) ?? undefined,
      fotoDocumentoId,
      presente,
      presenteMissa,
      frequenciaMissa,
    });
  }
}
